import type { PlanEntitlementsRow } from '../feature-flags/domain/planEntitlements';
import type { BranchAccess } from './store/useSessionStore';
import type { AccountSetupState, PendingEmployeeInvitation } from '../../auth/domain/accountSetup';

/**
 * ADR-017 Fase 4: SQLite eliminado como path de datos operacional.
 * `db` es un stub que retorna `undefined`/no-op en todas las plataformas.
 * El snapshot offline de cuenta siempre está vacío; los callers fallan back
 * a bootstrap/remoto.
 */

export type OfflineAccountSnapshot = {
  userId: string;
  /** Usuario completó onboarding de dueño (flag del servidor). */
  onboardingComplete: boolean;
  /** Gate autoritativo de onboarding (bootstrap / offline). */
  requiresOwnerOnboarding?: boolean | null;
  ownerSetupCompletedAt?: string | null;
  accountSetupOrganizationId?: string | null;
  accountSetupBranchCount?: number | null;
  accountSetupOrganizationClosedAt?: string | null;
  /** Invitación de empleado pendiente (bootstrap); null si no hay o no se cacheó. */
  pendingEmployeeInvitation?: PendingEmployeeInvitation | null;
  /** El usuario espera que lo inviten como empleado; no es dueño ni tiene org. */
  awaitingEmployeeInvitation?: boolean | null;
  entitlements: PlanEntitlementsRow | null;
  branches: BranchAccess[] | null;
  /** Último valor conocido; null = nunca cacheado (bootstrap puede seguir en null). */
  isEmployee: boolean | null;
  /**
   * True tras al menos un bootstrap exitoso con red (plan/sucursales/rol cacheado).
   * Permite arrancar sin `OfflineSetupRequired` cuando fallan RPC por falta de señal.
   */
  kioskOfflineReady?: boolean;
  updatedAt: number;
};

/** Si el snapshot ya permite considerar la cuenta como operativa sin llamadas de red. */
export async function snapshotImpliesOfflineOperational(snap: OfflineAccountSnapshot | null): Promise<boolean> {
  if (!snap) return false;
  if (snap.requiresOwnerOnboarding === true) return false;
  // Espera invitación: no tiene negocio propio, no hay nada operativo que abrir.
  if (snap.awaitingEmployeeInvitation === true && !snap.accountSetupOrganizationId) return false;
  if (snap.kioskOfflineReady === true && snap.requiresOwnerOnboarding === false) return true;
  if (snap.kioskOfflineReady === true) return true;
  if (snap.onboardingComplete && snap.requiresOwnerOnboarding === false) return true;
  if (typeof snap.isEmployee === 'boolean' && snap.isEmployee) return true;
  return false;
}

export async function getOfflineAccountSnapshot(_userId: string): Promise<OfflineAccountSnapshot | null> {
  return null;
}

export async function mergeOfflineAccountSnapshot(
  _userId: string,
  _partial: Partial<Omit<OfflineAccountSnapshot, 'userId' | 'updatedAt'>>
): Promise<void> {
  // No-op: SQLite eliminado (ADR-017 Fase 4).
}

export async function setOnboardingCompleteOffline(_userId: string): Promise<void> {
  // No-op: SQLite eliminado (ADR-017 Fase 4).
}

export async function saveAccountSetupOffline(_userId: string, _setup: AccountSetupState): Promise<void> {
  // No-op: SQLite eliminado (ADR-017 Fase 4).
}

export async function saveEntitlementsOffline(_userId: string, _row: PlanEntitlementsRow | null): Promise<void> {
  // No-op: SQLite eliminado (ADR-017 Fase 4).
}

export async function saveBranchesOffline(_userId: string, _branches: BranchAccess[]): Promise<void> {
  // No-op: SQLite eliminado (ADR-017 Fase 4).
}

/** `null` borra el campo cacheado (vuelve a estado desconocido). */
export async function saveIsEmployeeOffline(_userId: string, _isEmployee: boolean | null): Promise<void> {
  // No-op: SQLite eliminado (ADR-017 Fase 4).
}

export async function clearOfflineAccountSnapshot(_userId: string): Promise<void> {
  // No-op: SQLite eliminado (ADR-017 Fase 4).
}
